import { isBefore, subMonths } from 'date-fns'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { isBetweenInclusive } from 'components/common/date.utils'
import { LicenseAndSupportingDocumentOnly } from 'domain/models/AttendeeFormSchema/AccessRule'
import { MakeAttendeeFormSchemaContext } from 'domain/models/AttendeeFormSchema/MakeAttendeeFormSchemaContext'
import { UploadedDocumentKeySchema } from 'services/attendees/AttendeeForm/AttendeeFormSchema/UploadedDocumentKeySchema'
import { CompleteLicenseNowSchema } from 'services/attendees/AttendeeForm/AttendeeFormSchema/makeCompleteLicenseNowSchema'
import { matchGenericMinorSupportingDocumentSchemas } from 'services/attendees/AttendeeForm/AttendeeFormSchema/matchGenericSupportingDocumentSchemas'

const MedicalCertificateSchema = z.object({
  completeType: z.literal('MEDICAL_CERTIFICATE'),
  uploadedDocumentKey: UploadedDocumentKeySchema
})

const VerifiedFFAPPSSchema = z.object({
  verificationStatus: z.literal('VERIFIED'),
  verifiedAt: z.coerce.date()
})

const NotVerifiedFFAPPSSchema = z.object({
  verificationStatus: z.enum(['INVALID', 'REQUIRES_RENEWAL', 'UNVERIFIED']),
  uploadedDocumentKey: UploadedDocumentKeySchema
})

const FFAPPSVerificationSchema = z.discriminatedUnion('verificationStatus', [
  VerifiedFFAPPSSchema,
  NotVerifiedFFAPPSSchema
])

const FFAPPSSchema = z.object({
  completeType: z.literal('FFA_PPS'),
  documentNumber: z.string().min(1),
  documentVerification: FFAPPSVerificationSchema
})

const FFAPPSTooEarlySchema = z.object({
  completeType: z.literal('FFA_PPS_TOO_EARLY')
})

const FFA_DATE_OF_TRANSITION_TO_PPS = new Date('2024-03-31T22:00:00Z')
const FFA_DATE_OF_PPS_ENFORCEMENT = new Date('2024-08-31T22:00:00Z')

const isDateInFFAsPPSTransitionPeriod = isBetweenInclusive(
  FFA_DATE_OF_TRANSITION_TO_PPS,
  FFA_DATE_OF_PPS_ENFORCEMENT
)

export const makeFFACompleteLicenseAndSupportingDocumentNowSchema = (
  accessRule: LicenseAndSupportingDocumentOnly,
  ctx: MakeAttendeeFormSchemaContext
) => {
  const now = new Date()

  const isAttendeeMinor = ctx.attendeeAgeStatus === 'MINOR'
  const FFASpecificSchemas =
    isAttendeeMinor === true
      ? []
      : match(ctx)
          .when(
            ({ raceDate }) => isBefore(raceDate, FFA_DATE_OF_TRANSITION_TO_PPS),
            () => {
              return [MedicalCertificateSchema]
            }
          )
          .when(
            ({ raceDate, registrationOpeningDate }) =>
              isBefore(
                registrationOpeningDate,
                FFA_DATE_OF_TRANSITION_TO_PPS
              ) || isDateInFFAsPPSTransitionPeriod(raceDate),
            ({ raceDate }) => {
              const tooEarly = isBefore(now, subMonths(raceDate, 3))
              return [
                tooEarly ? FFAPPSTooEarlySchema : FFAPPSSchema,
                MedicalCertificateSchema
              ]
            }
          )
          .otherwise(({ raceDate }) => {
            const tooEarly = isBefore(now, subMonths(raceDate, 3))
            return [tooEarly ? FFAPPSTooEarlySchema : FFAPPSSchema]
          })
  return z.object({
    completeLater: z.literal(false),
    document: z.discriminatedUnion('completeType', [
      ...CompleteLicenseNowSchema(accessRule),
      ...matchGenericMinorSupportingDocumentSchemas(
        accessRule,
        isAttendeeMinor
      ),
      ...FFASpecificSchemas
    ])
  })
}
